import { useSelector } from "react-redux";
import { Select } from "risa-oasis-ui_v2";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { DropdownField } from "../../data-model/pharmaPaFormModel";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { useResolvedFieldOptions } from "../../hooks/useResolvedFieldOptions";
import { checkIfErrorExists } from "../../pages/pharmaPaForm/utils/checkIfErrorExists";
import { RootState } from "../../redux/store/store";
import { getNestedValue } from "../../utils/getNestedValue";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";

interface DropDownFieldProps {
  field: DropdownField;
  fieldDetails?: Record<string, any>;
  data?: any;
  originalData?: any;
  shouldEmitLogEvent?: boolean;
}
const RenderDropdownField = (props: DropDownFieldProps) => {
  const { formFieldsData, setFormFieldsData } = usePharmaFormFields();
  const { resolvedOptions } = useResolvedFieldOptions(props.field);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: singleMedicalPaFormConfig } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const initialDataValue = getNestedValue(props?.data, props.field.key) ?? "";

  const computeDefaultSelectValue = () => {
    if (props?.field?.readOnly) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? "";
    }
    if (formFieldsData?.[props?.field?.key]?.isFieldDirty) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? "";
    }
    if (initialDataValue && initialDataValue !== "") {
      return initialDataValue;
    } else {
      return props?.field?.default || "";
    }
  };

  const handleSelectFieldOnChange = (data: { value: any }) => {
    let newStateToUpdate = { ...formFieldsData }; // Copy the previous state
    const originalDataValue =
      getNestedValue(props?.originalData, props.field.key) ?? "";
    setFormFieldsData({
      ...newStateToUpdate,
      [props.field.key]: {
        ...newStateToUpdate[props.field.key],
        filledValue: data.value !== "" ? data.value : "",
        isFieldDirty: originalDataValue !== data.value,
      },
    });
    if (props?.shouldEmitLogEvent) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: props?.originalData?.patient_id,
        order_id: props?.originalData?.order_id,
        org_id: getOrgIdForFetchExternalWorklist(),
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          EventsName.FORM_FIELD_EDITED + " (" + props?.field?.label + ")",
        additional_data: {
          field_key: props?.field?.key,
          new_value: data.value,
          old_value: getNestedValue(props?.originalData, props.field.key) ?? "",
          form_id: singleMedicalPaFormConfig?.id ?? "",
          screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
          field_name: props?.field?.label ?? "",
          payer_name: payerName,
        },
      });
    }
  };
  return (
    <div
      key={props?.field?.key}
      className={`dropdown__container flex-1`}
      style={{
        maxWidth: props?.field?.width ? `${props.field.width}%` : "100%",
      }}
    >
      <Select
        id={props?.field?.key}
        onOptionChange={(data) => handleSelectFieldOnChange(data)}
        label={props?.field?.label}
        placeholder={props?.field?.placeholder ?? ""}
        options={resolvedOptions}
        required={props?.field?.isRequired}
        defaultValue={
          resolvedOptions?.some(
            (option) => option.value === computeDefaultSelectValue(),
          )
            ? computeDefaultSelectValue()
            : ""
        }
        error={
          checkIfErrorExists(
            formFieldsData,
            props?.field?.key,
            props?.field?.readOnly,
          )
            ? `${props?.field?.validationMessage ?? "This field is required"}`
            : ""
        }
      />
      {(props?.field?.additionalInfoHeader ||
        props?.field?.additionalInfoContent) && (
        <div className="section-additonal-info flex w-full flex-col gap-2 bg-secondaryYellow-11 p-3">
          <div className="section-additonal-info__header text-tiny font-bold">
            {props?.field?.additionalInfoHeader ?? ""}
          </div>
          <div className="section-additonal-info__content text-[0.8125rem] font-normal">
            {props?.field?.additionalInfoContent ?? ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderDropdownField;
